"use client";

import * as React from "react";
import { ArrowRight, Clock, FileText } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Card, CardContent, CardHeader, CardTitle, CardFooter } from "@/components/ui/card";
import { Link } from "@/components/common/Link";
import { PostsProvider, usePosts } from "@/integrations/wordpress/WordPressPostsProvider";
import { WP_Query } from "@/integrations/wordpress/wp_query";
function getReadTime(content?: string): string {
  if (!content) return "5 min read";
  const wordsPerMinute = 200;
  const words = content.trim().split(/\s+/).length;
  const time = Math.ceil(words / wordsPerMinute);
  return `${time} min read`;
}
function LatestInsightsConsumer() {
  const {
    posts,
    loading,
    error
  } = usePosts();
  if (loading) {
    return <div className="grid grid-cols-1 gap-8 md:grid-cols-3">
        {[...Array(3)].map((_, i) => <div key={i} data-index={i} className="flex flex-col gap-4 animate-pulse">
            <div className="bg-muted h-64 w-full rounded-xl" />
            <div className="h-4 w-24 bg-muted rounded" />
            <div className="h-8 w-3/4 bg-muted rounded" />
            <div className="h-4 w-full bg-muted rounded" />
          </div>)}
      </div>;
  }
  if (error) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>Unable to load insights at this time.</p>
      </div>;
  }
  if (posts.length === 0) {
    return <div className="text-center py-12 text-muted-foreground">
        <p>No insights found.</p>
      </div>;
  }
  return <div className="grid grid-cols-1 gap-x-8 gap-y-12 md:grid-cols-3">
      {posts.map((post, i) => <Card key={post.id} data-index={i} className="group border-none shadow-none bg-transparent p-0 rounded-none h-full">
          <CardHeader className="p-0 mb-6">
            <Link to={post.link || "#"} className="block overflow-hidden rounded-xl">
              {post.featuredImage ? <img src={post.featuredImage} alt={post.title} className="h-64 w-full object-cover transition-transform duration-700 group-hover:scale-105" /> : <div className="h-64 w-full bg-muted/50 flex items-center justify-center text-muted-foreground">
                  <FileText className="h-12 w-12 opacity-20" />
                </div>}
            </Link>
          </CardHeader>
          <CardContent className="p-0 flex flex-col flex-grow">
            <div className="flex items-center gap-3 mb-4">
              <Badge variant="secondary" className="rounded-full px-3 py-1 font-normal bg-accent/50 text-accent-foreground hover:bg-accent/70 border-none">
                <Clock className="mr-1.5 h-3 w-3" />
                {getReadTime(post.content || post.excerpt)}
              </Badge>
              {post.date && <span className="text-xs text-muted-foreground font-medium uppercase tracking-wider">
                  {new Date(post.date).toLocaleDateString("en-US", {
              month: "short",
              day: "numeric",
              year: "numeric"
            })}
                </span>}
            </div>

            <Link to={post.link || "#"} className="block group-hover:text-primary/80 transition-colors">
              <CardTitle className="font-serif text-2xl font-bold leading-tight text-primary mb-3">
                {post.title}
              </CardTitle>
            </Link>

            <div className="text-muted-foreground line-clamp-3 text-base leading-relaxed mb-4 flex-grow" dangerouslySetInnerHTML={{
          __html: post.excerpt || ""
        }} />
          </CardContent>
          <CardFooter className="p-0 mt-auto pt-2">
            <Link to={post.link || "#"}>
              <Button variant="link" className="p-0 h-auto font-semibold text-primary hover:text-secondary hover:no-underline group-hover:translate-x-1 transition-transform">
                Read Article <ArrowRight className="ml-2 h-4 w-4" />
              </Button>
            </Link>
          </CardFooter>
        </Card>)}
    </div>;
}
export default function LatestInsights() {
  const wp_query_params = {
    per_page: 3,
    orderby: "date",
    order: "desc"
  };
  const wp_query = new WP_Query(wp_query_params);
  return <section id="latest-insights" className="bg-background py-24 lg:py-32">
      <div className="container mx-auto px-6 md:px-12 max-w-7xl">
        <div className="flex flex-col md:flex-row md:items-end justify-between mb-16 gap-6">
          <div className="max-w-2xl">
            <h2 className="font-serif text-4xl md:text-5xl font-bold text-primary mb-6">
              Latest Insights
            </h2>
            <p className="text-lg text-muted-foreground max-w-xl">
              Perspectives on lifelong learning, organizational capability, and the future of higher education.
            </p>
          </div>
        </div>

        <PostsProvider wp_query={{
        per_page: 3,
        orderby: "date",
        order: "desc"
      }}>
          <LatestInsightsConsumer />
        </PostsProvider>

        <div className="mt-20 border-t border-border pt-12">
          <Link to="/InsightsPage" className="block w-full">
            <Button variant="ghost" className="w-full h-auto py-8 text-xl md:text-2xl font-serif text-primary hover:bg-accent/30 hover:text-secondary transition-all justify-between group border-b border-transparent hover:border-secondary/20 rounded-none px-4">
              <span className="font-medium">View All Insights</span>
              <ArrowRight className="h-6 w-6 md:h-8 md:w-8 transition-transform group-hover:translate-x-2" />
            </Button>
          </Link>
        </div>
      </div>
    </section>;
}